// src/ui/VaultButton.jsx — the code, as something you control.
//
// 🟥 BEFORE THIS, THE ONLY WAY ANYONE MET THE VAULT WAS THE UNLOCK SCREEN. It
//    appeared after an idle lock and that was the whole relationship: nothing
//    offered a code to someone who had not set one, nothing changed it, nothing
//    took it away, and nothing let you lock on purpose when you stood up. A
//    security control that only ever interrupts people is one they learn to
//    resent, and then to disable.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock, ShieldLock } from './icons';
import { vaultState, setCode, changeCode, removeCode, lockNow } from '../services/vaultAdmin';
import './pairing.css';

export default function VaultButton() {
  const [state, setState] = useState('off');   // off | locked | unlocked
  const [menu, setMenu] = useState(false);
  const [form, setForm] = useState(null);      // null | 'set' | 'change' | 'remove'
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const wrap = useRef(null);

  const refresh = useCallback(async () => { setState(await vaultState()); }, []);

  /* 🟢 POLLED, BECAUSE THE LOCK HAPPENS SOMEWHERE ELSE. The idle guard drops the
     key on its own schedule; without re-reading, this button would still be
     offering "Change code" for a vault that had closed underneath it. */
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    if (!menu && !form) return undefined;
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) { setMenu(false); setForm(null); } };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [menu, form]);

  const reset = () => { setPin(''); setConfirm(''); setErr(''); setBusy(false); };
  const open = (which) => { reset(); setForm(which); setMenu(false); };
  const say = (m) => { setFlash(m); setTimeout(() => setFlash(''), 2600); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(''); setBusy(true);
    try {
      if (form === 'set' || form === 'change') {
        if (pin.length < 4) { setErr('Use at least 4 digits.'); return; }
        /* 🟥 CONFIRMED, BECAUSE NOTHING CAN RECOVER IT. A mistyped code here is not
           a password anybody can reset — it is the only key to data on this
           machine, and Allo never sees it. */
        if (pin !== confirm) { setErr('Those do not match.'); return; }
        if (form === 'set') await setCode(pin); else await changeCode(pin);
        say(form === 'set' ? 'Code set' : 'Code changed');
      } else if (form === 'remove') {
        const { media, history } = await removeCode();
        say(`Code removed · ${media + history} item${media + history === 1 ? '' : 's'} unlocked`);
      }
      setForm(null); reset(); refresh();
    } catch (e2) {
      setErr(e2?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const title = state === 'off' ? 'Not locked on this computer'
    : state === 'locked' ? 'Locked' : 'Locked when idle';

  return (
    <div className="sb-menu-wrap vb-wrap" ref={wrap}>
      <button
        className={`icon-btn sb-act ${menu || form ? 'lit' : ''} ${state === 'unlocked' ? 'vb-on' : ''}`}
        title={title} aria-label={title}
        onClick={() => { setForm(null); setMenu(!menu); refresh(); }}
      >
        {state === 'off' ? <Lock width={17} height={17} /> : <ShieldLock width={18} height={18} />}
      </button>

      {menu && !form && (
        <div className="sb-menu vb-menu">
          {state === 'off' && (
            <>
              <button onClick={() => open('set')}><ShieldLock width={15} height={15} /> Set a code</button>
              <p className="vb-note">
                Messages and media are kept on this computer so they load instantly. A
                code encrypts them and is forgotten after 30 minutes idle. Without one,
                they are erased after a day instead.
              </p>
            </>
          )}
          {state === 'unlocked' && (
            <>
              {/* 🟢 "I am walking away" is something a person knows long before any
                  timer could infer it — and a lock you cannot ask for is one you
                  do not trust. */}
              <button onClick={() => { setMenu(false); lockNow(); refresh(); say('Locked'); }}>
                <Lock width={15} height={15} /> Lock now
              </button>
              <button onClick={() => open('change')}><ShieldLock width={15} height={15} /> Change code</button>
              <button className="danger" onClick={() => open('remove')}><Lock width={15} height={15} /> Remove code</button>
            </>
          )}
          {state === 'locked' && (
            <p className="vb-note">Locked. Enter your code on the unlock screen to continue.</p>
          )}
        </div>
      )}

      {form && (
        <form className="sb-menu vb-form" onSubmit={submit}>
          <h4>{form === 'set' ? 'Set a code' : form === 'change' ? 'Change your code' : 'Remove your code?'}</h4>

          {form === 'remove' ? (
            /* 🟥 SAID BEFORE IT HAPPENS, NOT AFTER. Removing the code decrypts what
               is stored here and leaves it readable — that is the point, and it is
               also the thing somebody might not have realised they were asking for. */
            <p className="vb-note">
              What Allo has stored here will be decrypted and left readable, and this
              computer will go back to erasing it after a day of not being used.
            </p>
          ) : (
            <>
              <label className="vg-field">
                <span>{form === 'change' ? 'New code' : 'Code'}</span>
                <input type="password" inputMode="numeric" autoComplete="off" autoFocus
                  value={pin} onChange={(e) => setPin(e.target.value)} disabled={busy} />
              </label>
              <label className="vg-field">
                <span>Type it again</span>
                <input type="password" inputMode="numeric" autoComplete="off"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy} />
              </label>
            </>
          )}

          {err && <div className="vg-err">{err}</div>}
          <button className="vg-go" type="submit" disabled={busy || (form !== 'remove' && pin.length < 4)}>
            {busy ? 'Working…' : form === 'remove' ? 'Remove it' : 'Save'}
          </button>
          <button className="vg-skip" type="button" onClick={() => { setForm(null); reset(); }} disabled={busy}>
            Cancel
          </button>
        </form>
      )}

      {flash && <div className="vb-flash">{flash}</div>}
    </div>
  );
}
